import React, {useEffect, useReducer, useRef} from 'react';
import { Table, Alert } from 'react-bootstrap';
import PrinterStates from './PrinterStates';
import API, {DbHandler} from '../services/API';
import type {PrinterHistoryResult} from "../services/API";
import { RouteComponentProps} from "react-router-dom";
import {Loading} from "./Components";

const reducer = (state, action) => {
    switch(action.type) {
        case 'loading':
            return { ...state, loading: true};
        case 'loaded-more': {
            const data: PrinterHistoryResult & { newEntries: boolean } = action.payload;
            const rows = data.history || [];
            return {
                ...state,
                loading: false,
                // first to najnowsze zdarzenie, last najstarsze. Doczytywanie starszych nie rusza first,
                // doczytywanie nowszych nie rusza last.
                first: data.newEntries ? data.first : (state.first || data.first),
                last: data.newEntries ? (state.last || data.last) : data.last,
                isMore: data.newEntries ? state.isMore : data.more,
                // added i loads opisują wyłącznie doczytywanie starszych wpisów. Odświeżenie nowszych
                // ich nie rusza, żeby nie uruchomić pobierania kolejnej strony.
                added: data.newEntries ? state.added : rows.length,
                loads: data.newEntries ? state.loads : state.loads + 1,
                history: data.newEntries ? [...rows, ...state.history] : [...state.history, ...rows]
            };
        }
        case 'loaded-end':
            return { ...state, isMore: false, loading: false} ;
        default: throw new Error('not handled action: ' + action);
    }
}
/** Liczba zdarzeń pobieranych jednym zapytaniem */
const PAGE_SIZE = 30;
const initialState = { history: [], last: null, first: null, loading: false, isMore: true, added: 0, loads: 0 }

type Props = RouteComponentProps & {
    printer: number;
    /** false gdy tytuł niesie już etykieta zakładki */
    showHeader?: boolean;
}

const PrinterHistory = (props: Props) => {

    const showHeader = props.showHeader !== false;

    const [state, dispatch] = useReducer(reducer, initialState);
    // Uchwyty zdarzeń rejestrujemy raz, a potrzebują bieżącego stanu. Domknięcie trzymałoby ten z montowania.
    const stateRef = useRef(state);
    stateRef.current = state;

    const getData = (fromParam, limitParam) => {
        if (stateRef.current.loading) return;
        dispatch({type: 'loading'});
        const newEntries = typeof limitParam !== 'undefined';
        const from = newEntries ? fromParam : stateRef.current.last;
        const limit = newEntries ? limitParam : PAGE_SIZE;

        API.getPrinterHistory({ printerId: props.printer, from, limit }).then(result=>{
          if (result) dispatch({type: 'loaded-more', payload: {...result, newEntries}})
          else dispatch({type: 'loaded-end'})
        });
    }

    useEffect(()=>{
        const onScroll = () => {
            const scrolledToBottom = (window.innerHeight + window.pageYOffset) >= (document.body.offsetHeight);
            if (scrolledToBottom && stateRef.current.isMore) getData(null);
        }

        window.addEventListener('scroll', onScroll, false);
        const handler: DbHandler = API.clientApi.registerHandler('printerevent', (change, id)=>{
            const first = stateRef.current.first;
            if (first) getData(first, null);
            else console.log('got printerhistory change before first data was loaded, not refreshing');
        });

        getData(null);
        return () => {
            handler.unregister();
            window.removeEventListener('scroll', onScroll, false);
        }
    }, []);

    // Strona wycięta w całości przez filtr stanów pomijanych nie doda ani jednego wiersza.
    // Nie ma wtedy czego przewinąć, więc kolejną stronę bierzemy sami.
    useEffect(()=>{
        if (!state.loading && state.isMore && state.loads > 0 && state.added === 0) getData(null);
    }, [state.loading, state.isMore, state.loads, state.added]);

    if (!state.history || !state.history.length) return <div className="settings-section printer-history">
        {showHeader ? <h2>Historia drukarki</h2> : null}
        <Alert variant="primary">Brak danych o historii drukarki</Alert>
    </div>;

    return <div className="settings-section printer-history">
        {showHeader ? <h2>Historia drukarki</h2> : null}
        <Table responsive striped hover>
        <thead>
          <tr>
            <th>Data</th>
            <th>Stan</th>
          </tr>
        </thead>
        <tbody>
          {
            state.history.map((row, idx)=><tr key={idx}>
                <td>{row.date}</td>
                <td><PrinterStates key={row.idx} short={false} status={row.errorStates}
                                   jamLocations={row.jamLocations} serviceCauses={row.serviceCauses}
                                   offlineCauses={row.offlineCauses} /></td>
            </tr>)
          }
          {state.loading && <tr><td colSpan="2"><Loading /></td></tr>}
        </tbody>
        </Table>
    </div>
}

export default PrinterHistory;